import { RenderingEngine, Enums } from "@cornerstonejs/core";
import { drawOverlayPure } from "./drawOverlay_l";

/** DEBUG switch */
const LOG = true;
const dbg = (...args) => LOG && console.log("[snapshots]", ...args);

/** Pick the 0-based slice index for a finding (Genesis shapes supported) */
export function getSliceIndexForFinding(f) {
  if (Number.isInteger(f?.sliceIndex)) return f.sliceIndex;
  if (f?.grain && Number.isInteger(f.grain.main_slice))
    return f.grain.main_slice - 1; // 1-based -> 0-based
  if (Number.isInteger(f?.slice)) return Math.max(0, f.slice - 1);
  return null;
}

/** Collect "Genesis AI" findings from annotationsData */
export function collectGenesisFindings(annotationsData) {
  return Object.values(annotationsData || {})
    .flat()
    .filter((a) => a?.creator === "Genesis AI" && a?.type === "finding");
}

// ------------------------------
// Cropping helpers (no camera zoom → spacing intact)
// ------------------------------
function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

/**
 * Compute a crop rect (canvas px) around the lungs mask for this slice.
 * Falls back to a central crop when mask is missing.
 */
function getLungsCropRect({
  slice,
  meta,
  viewport,
  canvasW,
  canvasH,
  padRatio = 0.12,
  fallbackFrac = 0.85,
}) {
  const {
    rows,
    columns,
    pixelSpacing = [1, 1],
    imagePositionPatient = [0, 0, 0],
    imageOrientationPatient = [1, 0, 0, 0, 1, 0],
  } = meta || {};

  const psR = pixelSpacing[0];
  const psC = pixelSpacing[1];
  const iop = imageOrientationPatient;

  const imgToWorld = (col, row) => [
    imagePositionPatient[0] + col * psC * iop[3] + row * psR * iop[0],
    imagePositionPatient[1] + col * psC * iop[4] + row * psR * iop[1],
    imagePositionPatient[2],
  ];

  const toCanvas = (col, row) => {
    const [wx, wy, wz] = imgToWorld(col, row);
    const [cx, cy] = viewport.worldToCanvas([wx, wy, wz]);
    return [cx, cy];
  };

  if (
    slice?.lungs?.indices?.length &&
    Number.isInteger(columns) &&
    Number.isInteger(rows)
  ) {
    let minCol = columns,
      maxCol = 0,
      minRow = rows,
      maxRow = 0;
    for (const idx of slice.lungs.indices) {
      const col = idx % columns;
      const row = (idx / columns) | 0;
      if (col < minCol) minCol = col;
      if (col > maxCol) maxCol = col;
      if (row < minRow) minRow = row;
      if (row > maxRow) maxRow = row;
    }

    const corners = [
      toCanvas(minCol, minRow),
      toCanvas(maxCol, minRow),
      toCanvas(minCol, maxRow),
      toCanvas(maxCol, maxRow),
    ];

    const xs = corners.map((p) => p[0]);
    const ys = corners.map((p) => p[1]);

    let x = Math.min(...xs);
    let y = Math.min(...ys);
    let w = Math.max(...xs) - x;
    let h = Math.max(...ys) - y;

    const padX = w * padRatio;
    const padY = h * padRatio;

    x = clamp(x - padX, 0, canvasW);
    y = clamp(y - padY, 0, canvasH);
    w = clamp(w + padX * 2, 1, canvasW - x);
    h = clamp(h + padY * 2, 1, canvasH - y);

    dbg("lungs bbox (canvas px)", {
      x: Math.round(x),
      y: Math.round(y),
      w: Math.round(w),
      h: Math.round(h),
    });

    return {
      x: Math.round(x),
      y: Math.round(y),
      w: Math.round(w),
      h: Math.round(h),
    };
  }

  const w = Math.round(canvasW * fallbackFrac);
  const h = Math.round(canvasH * fallbackFrac);
  const x = Math.round((canvasW - w) / 2);
  const y = Math.round((canvasH - h) / 2);
  dbg("fallback central crop", { x, y, w, h });
  return { x, y, w, h };
}

/** Wait for an IMAGE_RENDERED event with a timeout so we never hang silently */
function waitRenderedWithTimeout(targetEl, label = "", timeoutMs = 4000) {
  return new Promise((resolve) => {
    let done = false;
    const eventName = Enums.Events.IMAGE_RENDERED;

    const onDone = (reason) => {
      if (done) return;
      done = true;
      try {
        targetEl?.removeEventListener?.(eventName, onEvent);
      } catch {}
      dbg("render completed:", label || "(no label)", reason);
      resolve(void 0);
    };

    const onEvent = () => onDone("event");

    try {
      targetEl?.addEventListener?.(eventName, onEvent, { once: true });
    } catch (e) {
      dbg("WARN could not attach listener:", e);
    }

    setTimeout(() => {
      if (!done) {
        dbg("WARN render timed out:", label, `>${timeoutMs}ms`);
        onDone("timeout");
      }
    }, timeoutMs);
  });
}

/**
 * Build CT+overlay snapshots OFF-SCREEN (keeps live viewer untouched).
 * No camera zoom is used; we crop to lungs so they look bigger without changing pixel spacing.
 */
export async function buildSnapshotsOnce({
  imageIds,
  meta,
  examData,
  findings,
  size = { width: 1024, height: 1024 },
  maxWidth = 640,
}) {
  dbg("start buildSnapshotsOnce", {
    imageCount: imageIds?.length,
    hasMeta: !!meta,
    hasExamData: !!examData,
    findingsCount: findings?.length,
    size,
    maxWidth,
  });

  if (!imageIds?.length || !meta || !examData || !findings?.length) {
    const msg = "Missing prerequisites (imageIds/meta/examData/findings).";
    dbg("ABORT:", msg);
    return { snapshots: [], errors: [msg] };
  }

  // Off-screen element
  const el = document.createElement("div");
  el.style.position = "fixed";
  el.style.left = "-99999px";
  el.style.top = "0";
  el.style.width = `${size.width}px`;
  el.style.height = `${size.height}px`;
  el.style.overflow = "hidden";
  document.body.appendChild(el);
  dbg("off-screen element attached", { w: size.width, h: size.height });

  const reId = `re_${Date.now()}`;
  const vpId = `vp_${Date.now()}`;
  dbg("create RenderingEngine", { reId, vpId });

  const engine = new RenderingEngine(reId);

  engine.enableElement({
    viewportId: vpId,
    type: Enums.ViewportType.STACK,
    element: el,
  });

  const viewport = engine.getViewport(vpId);
  dbg("viewport enabled", {
    canvasPx: { w: viewport.canvas?.width, h: viewport.canvas?.height },
  });

  // Load stack
  dbg("setStack -> waiting first render …");
  await viewport.setStack(imageIds);
  viewport.resize();
  await waitRenderedWithTimeout(viewport.element || el, "initial setStack");

  const snapshots = [];
  const errors = [];

  const toDataUrlDownscaled = (canvas) => {
    if (canvas.width <= maxWidth) {
      dbg("downscale not needed", { w: canvas.width, h: canvas.height });
      return canvas.toDataURL("image/png");
    }
    const scale = maxWidth / canvas.width;
    const w = Math.round(canvas.width * scale);
    const h = Math.round(canvas.height * scale);
    const r = document.createElement("canvas");
    r.width = w;
    r.height = h;
    const ctx = r.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(canvas, 0, 0, w, h);
    dbg("downscaled", {
      from: { w: canvas.width, h: canvas.height },
      to: { w, h },
    });
    return r.toDataURL("image/png");
  };

  try {
    dbg("loop findings start", { count: findings.length });
    console.time("[snapshots] total");

    for (let i = 0; i < findings.length; i++) {
      const f = findings[i];
      console.time(`[snapshots] finding#${i}`);

      const sliceIndex = getSliceIndexForFinding(f);
      dbg("finding", i, {
        id: f?.id,
        sliceIndex,
        valid:
          Number.isInteger(sliceIndex) &&
          sliceIndex >= 0 &&
          sliceIndex < imageIds.length,
      });

      if (
        !Number.isInteger(sliceIndex) ||
        sliceIndex < 0 ||
        sliceIndex >= imageIds.length
      ) {
        const err = `Finding ${f?.id ?? i}: invalid slice index`;
        dbg("ERROR", err);
        errors.push(err);
        console.timeEnd(`[snapshots] finding#${i}`);
        continue;
      }

      // Render target slice
      dbg("setImageIdIndex -> wait render", { sliceIndex });
      viewport.setImageIdIndex(sliceIndex);
      await waitRenderedWithTimeout(
        viewport.element || el,
        `slice ${sliceIndex}`
      );

      // Paint overlay
      const overlay = document.createElement("canvas");
      drawOverlayPure({
        viewport,
        sliceIndex,
        meta,
        examData,
        canvas: overlay,
      });
      dbg("overlay drawn", {
        overlayPx: { w: overlay.width, h: overlay.height },
      });

      // Composite (CSS pixel aligned)
      const rect = viewport.canvas.getBoundingClientRect();
      const merged = document.createElement("canvas");
      merged.width = overlay.width || Math.ceil(rect.width);
      merged.height = overlay.height || Math.ceil(rect.height);
      const mctx = merged.getContext("2d");
      mctx.drawImage(viewport.canvas, 0, 0, merged.width, merged.height);
      mctx.drawImage(overlay, 0, 0);
      dbg("merged composed", {
        mergedPx: { w: merged.width, h: merged.height },
      });

      // Crop around lungs
      const sliceNum = sliceIndex + 1;
      const sliceObj = examData?.jsonData?.slices?.find(
        (s) => s.sliceNum === sliceNum
      );

      let outputCanvas = merged;
      if (sliceObj) {
        const { x, y, w, h } = getLungsCropRect({
          slice: sliceObj,
          meta,
          viewport,
          canvasW: merged.width,
          canvasH: merged.height,
          padRatio: 0.12,
          fallbackFrac: 0.85,
        });
        const cropped = document.createElement("canvas");
        cropped.width = w;
        cropped.height = h;
        cropped.getContext("2d").drawImage(merged, x, y, w, h, 0, 0, w, h);
        outputCanvas = cropped;
        dbg("cropped", { x, y, w, h });
      } else {
        dbg("no sliceObj mask, using merged as-is");
      }

      const dataUrl = toDataUrlDownscaled(outputCanvas);

      const snap = {
        noduleId: f?.id,
        sliceIndex,
        dataUrl,
        fields: {
          diameterMm: f?.grain?.size?.mm ?? null,
          volumeMm3: f?.grain?.size?.mm3 ?? null,
          location: f?.location ?? null,
          lungRads: f?.lung_rads_score ?? null,
          type: f?.type || "Solid",
          note: f?.note ?? null,
        },
      };

      dbg("snapshot ready", {
        i,
        noduleId: snap.noduleId,
        sliceIndex: snap.sliceIndex,
      });
      snapshots.push(snap);
      console.timeEnd(`[snapshots] finding#${i}`);
    }

    console.timeEnd("[snapshots] total");
    dbg("loop findings end", {
      snapshots: snapshots.length,
      errors: errors.length,
    });
  } catch (e) {
    const err = e?.message || String(e);
    dbg("FATAL", err, e);
    errors.push(err);
  }

  // Cleanup
  try {
    engine?.destroy?.();
    dbg("engine destroyed");
  } catch (e) {
    dbg("engine destroy error", e);
  }
  try {
    if (el.parentNode) el.parentNode.removeChild(el);
    dbg("off-screen element removed");
  } catch (e) {
    dbg("off-screen element removal error", e);
  }

  return { snapshots, errors };
}
